import React, { FC, useEffect, useState } from 'react'
import {
	Alert,
	ColumnLayout,
	Grid,
	Header,
	RadioGroup
} from '@cloudscape-design/components'
import { TagsApproval } from '../../../types/tags'
import useMultiStepApprovalTags from '../../../hooks/mutate/useMultiStepApprovalTags'
import { useOktaAuth } from '@okta/okta-react'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import hasRequiredPermissions from '../../../helper/hasRequiredPermissions'
import {
	getRequiredApproverEmail,
	getTagApproverLabel
} from '../../../helper/applicationTagApproval'
import { type PlatformOwnerResource } from '../../../helper/platformOwnerPermissions'
interface TagsApprovalProps {
	approvalStatus: Array<TagsApproval> | []
	isDisabled?: boolean
	allowPlatformOwnerApproval?: boolean
	platformOwnerResource?: PlatformOwnerResource
	tagsID?: string
	description?: string
	// Tag details, used only to resolve who is allowed to approve a per-application entry.
	tagDetails?: Record<string, any>
}

const approvalKey = (approval: TagsApproval) =>
	// Checks if application name exists, if it does it uses ApplicationOwner:app-name else PlatformOwner/CloudEco/Finance
	approval.application ? `${approval.name}:${approval.application}` : approval.name

const persistApprovalStatus = (
	approvalList: Array<TagsApproval>,
	tagsID?: string
) => {
	if (!tagsID) {
		return
	}

	try {
		localStorage.setItem(
			`approvalStatus:tags:${tagsID}`,
			JSON.stringify(approvalList)
		)
		window.dispatchEvent(
			new CustomEvent('approvalStatusUpdated', {
				detail: approvalList
			})
		)
	} catch (e) {
		// ignore storage failures
	}
}

const ApproverOverviewLocal: FC<TagsApprovalProps> = ({
	approvalStatus,
	isDisabled = false,
	allowPlatformOwnerApproval = false,
	tagsID = '',
	tagDetails = {}
}) => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const { mutate: updateMultiStepTagsById } = useMultiStepApprovalTags(tagsID)
	const { data: access } = useUserPermissions(thisUserEmail)

	const canApproveForTeam = (approval: TagsApproval): boolean => {
		const permissions = access?.permissions || []

		// Platform Owner and Application Owner can approve only when the signed-in
		// email is the one stored for that role.
		const requiredApproverEmail = getRequiredApproverEmail(approval, tagDetails)
		if (
			approval.name === 'PlatformOwner' ||
			approval.name === 'ApplicationOwner'
		) {
			if (requiredApproverEmail) {
				return (
					thisUserEmail?.toLowerCase() === requiredApproverEmail.toLowerCase()
				)
			}
			// Application Owner is always a named person. With no email stored, nobody
			// can approve that row. Platform Owner with no email keeps the team check.
			if (approval.name === 'ApplicationOwner') {
				return false
			}
		}

		if (approval.application) {
			return false
		}

		return (
			hasRequiredPermissions([`tags:${approval.name}:*`], permissions) ||
			hasRequiredPermissions(['tags:*:approve'], permissions)
		)
	}

	const canEditApproval = (approval: TagsApproval): boolean => {
		if (isDisabled) {
			const isOwnerRow =
				approval.name === 'PlatformOwner' ||
				approval.name === 'ApplicationOwner'
			return (
				allowPlatformOwnerApproval &&
				isOwnerRow &&
				canApproveForTeam(approval)
			)
		}
		return canApproveForTeam(approval)
	}

	const [notification, setNotification] = useState<any>({
		type: '',
		statusIconAriaLabel: '',
		msg: ''
	})
	const [showApprovalNotification, setShowApprovalNotification] =
		useState(false)

	const [approvalStatusLocal, setApprovalStatus] = useState(approvalStatus)

	useEffect(() => {
		setApprovalStatus(approvalStatus)
	}, [approvalStatus])

	useEffect(() => {
		persistApprovalStatus(approvalStatusLocal, tagsID)
	}, [approvalStatusLocal, tagsID])

	const updateApprovalStatus = (state: any, target: TagsApproval) => {
		if (!canApproveForTeam(target)) {
			return
		}
		const targetKey = approvalKey(target)
		let updatedApprovalList: Array<TagsApproval> = []
		approvalStatusLocal.forEach((item) => {
			// Match on name AND application: several entries can share a name on a GCP
			// Project tag record shared by multiple applications.
			if (approvalKey(item) === targetKey) {
				updatedApprovalList.push({
					...item,
					status:
						state.value === 'default' ? null : state.value === 'true' ? 1 : 0,
					approverID: thisUserEmail,
					lastUpdated: Date.now() + ''
				})
			} else {
				updatedApprovalList.push(item)
			}
		})

		setApprovalStatus(updatedApprovalList)

		updateMultiStepTagsById(
			{
				tagsID: tagsID,
				approvalStatus: updatedApprovalList.find(
					(item) => approvalKey(item) === targetKey
				),
				action: 'UPDATE_APPROVER_STATUS'
			},
			{
				onSuccess: () => {
					console.log('success')
					setNotification({
						type: 'success',
						statusIconAriaLabel: 'Success',
						msg: 'Tags Approval Status Updated!'
					})
					setShowApprovalNotification(true)
					setTimeout(() => {
						setShowApprovalNotification(false)
					}, 3000)
				},
				onError: (e) => {
					setNotification({
						type: 'error',
						statusIconAriaLabel: 'Error',
						msg: 'Error saving Tags, Please try again!'
					})
					setShowApprovalNotification(true)
					setTimeout(() => {
						setShowApprovalNotification(false)
					}, 3000)
				}
			}
		)
	}
	return (
		<>
			<Header variant="h3" description="">
				Approval Status
			</Header>
			<ColumnLayout columns={2} variant="text-grid">
				{approvalStatusLocal &&
					approvalStatusLocal.map((approverItem) => {
						return (
							<Grid
								disableGutters
								gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
								key={approvalKey(approverItem)}
							>
								<div
									color="text-body-secondary"
									key={approvalKey(approverItem)}
								>
									{getTagApproverLabel(approverItem.name)}
								</div>

								<RadioGroup
									onChange={({ detail }) => {
										updateApprovalStatus(detail, approverItem)
									}}
									value={
										approverItem.status === null
											? 'default'
											: Boolean(approverItem.status) === true
											? 'true'
											: 'false'
									}
									items={[
										{
											value: 'default',
											label: 'Pending Action',
											disabled: true
										},
										{
											value: 'true',
											label: isDisabled ? 'Approved' : 'Approve',
											disabled: !canEditApproval(approverItem)
										},
										{
											value: 'false',
											label: isDisabled ? 'Rejected' : 'Reject',
											disabled: !canEditApproval(approverItem)
										}
									]}
								/>
								<Grid>
									<div>
										{approverItem.status === 1 && <p>Approved By  :  {approverItem.approverID}</p>}
										{approverItem.status === 0 && <p>Rejected By :  {approverItem.approverID}</p>}
									</div>
								</Grid>
							</Grid>
						)
					})}
			</ColumnLayout>
			{showApprovalNotification && (
				<>
					<br />
					<Alert
						statusIconAriaLabel={notification.statusIconAriaLabel}
						type={notification.type}
					>
						{notification.msg}
					</Alert>
				</>
			)}
		</>
	)
}

const ApproverOverview = React.memo(ApproverOverviewLocal)
export default ApproverOverview
